
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Channel, Group } from "@/lib/types";
import { toast } from "sonner";
import { safeMaybeSingle } from "@/utils/supabase/queries";
import { toDatabaseValue, isDatabaseRecord } from "@/utils/supabase/type-guards";

export function useChannel(channelId: string | undefined) {
  const [channel, setChannel] = useState<Channel | null>(null);
  const [group, setGroup] = useState<Group | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!channelId) {
      setLoading(false);
      return;
    }

    const fetchChannelAndGroup = async () => {
      try {
        // Fetch channel data
        const channelData = await safeMaybeSingle<any>(
          supabase.from("channels").select("*").eq("id", toDatabaseValue(channelId)),
          "Failed to load channel"
        );

        if (channelData && isDatabaseRecord(channelData)) {
          const channelInfo: Channel = {
            id: channelData.id,
            groupId: channelData.group_id,
            name: channelData.name,
            description: channelData.description,
            isAnnouncement: channelData.is_announcement,
            isTemporary: channelData.is_temporary,
            autoDeleteAfter: channelData.auto_delete_after,
            createdAt: channelData.created_at,
            createdBy: channelData.created_by,
            updatedAt: channelData.updated_at,
          };
          setChannel(channelInfo);

          // Fetch group data
          const groupData = await safeMaybeSingle<any>(
            supabase.from("groups").select("*").eq("id", toDatabaseValue(channelData.group_id)),
            "Failed to load group"
          );

          if (groupData && isDatabaseRecord(groupData)) {
            const groupInfo: Group = {
              id: groupData.id,
              name: groupData.name,
              description: groupData.description,
              rules: groupData.rules,
              createdAt: groupData.created_at,
              createdBy: groupData.created_by,
              updatedAt: groupData.updated_at,
            };
            setGroup(groupInfo);
          }
        }
      } catch (error) {
        console.error("Error fetching channel and group:", error);
        toast.error("Failed to load channel details");
      } finally {
        setLoading(false);
      }
    };

    fetchChannelAndGroup();
  }, [channelId]);

  return {
    channel,
    group,
    loading,
  };
}
